// SS PWA — components/diagnostics/HealthBadges.tsx — 2026-06-03
//
// Operator-only health badges. Shows raw counters + status. NOT used in
// the client-facing /health view (see HealthStatusBanner / OutcomeCard).

import type { HealthResponse } from '@/lib/api/diagnostics';

interface HealthBadgesProps {
  health: HealthResponse | null;
  loading?: boolean;
}

function statusColor(s: 'green' | 'yellow' | 'red'): string {
  if (s === 'green') return 'bg-emerald-500';
  if (s === 'yellow') return 'bg-amber-500';
  return 'bg-red-500';
}

function metricChip(label: string, value: string | number, tone: 'ok' | 'warn' | 'crit' = 'ok') {
  const toneClasses =
    tone === 'crit'
      ? 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900'
      : tone === 'warn'
      ? 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900'
      : 'bg-ink-50 text-ink-700 ring-ink-200 dark:bg-[#1F2A37] dark:text-[#E2E8F0] dark:ring-[#2A374A]';
  return (
    <span
      key={label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${toneClasses}`}
    >
      <span className="font-medium">{label}</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}

export function HealthBadges({ health, loading }: HealthBadgesProps) {
  if (loading || !health) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-3 text-sm text-ink-500 dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-[#94A3B8]">
        {loading ? 'Loading health…' : 'No health data yet.'}
      </div>
    );
  }
  const c = health.counters_last_hour;
  const m = health.minutes_since;
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-3 dark:border-[#1F2A37] dark:bg-[#0F172A]">
      <div className="mb-2 flex items-center gap-2">
        <span className={`inline-block size-2.5 rounded-full ${statusColor(health.status)}`} />
        <span className="text-sm font-semibold text-ink-900 dark:text-white">{health.tenant.display_name}</span>
        <span className="text-xs text-ink-500 dark:text-[#94A3B8]">{health.tenant.slug}</span>
        <span className="ml-auto text-xs text-ink-400">{new Date(health.generated_at).toLocaleTimeString()}</span>
      </div>
      {health.reasons.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1">
          {health.reasons.map((r) => metricChip(r, '', 'warn'))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {metricChip('calls/h', c.calls_received ?? 0)}
        {metricChip('sms in/h', c.sms_received ?? 0)}
        {metricChip('mctb/h', c.mctb_fired ?? 0)}
        {metricChip('push/h', c.push_dispatched ?? 0)}
        {metricChip('429/h', c['429_count'] ?? 0, (c['429_count'] ?? 0) > 0 ? 'warn' : 'ok')}
        {metricChip('502/h', c['502_count'] ?? 0, (c['502_count'] ?? 0) > 0 ? 'warn' : 'ok')}
        {metricChip('kv fail/h', c.kv_failure ?? 0, (c.kv_failure ?? 0) > 0 ? 'crit' : 'ok')}
        {metricChip('d1 fail/h', c.d1_failure ?? 0, (c.d1_failure ?? 0) > 0 ? 'crit' : 'ok')}
        {m.call != null ? metricChip('last call', `${m.call}m`) : null}
        {m.sms_in != null ? metricChip('last sms', `${m.sms_in}m`) : null}
        {m.push != null ? metricChip('last push', `${m.push}m`) : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-xs text-ink-500 dark:text-[#94A3B8]">
        <span>bindings:</span>
        <span className={health.bindings.db ? 'text-emerald-600' : 'text-red-500'}>D1{health.bindings.db ? '✓' : '✗'}</span>
        <span className={health.bindings.sms_state_kv ? 'text-emerald-600' : 'text-red-500'}>SMS KV{health.bindings.sms_state_kv ? '✓' : '✗'}</span>
        <span className={health.bindings.push_subs_kv ? 'text-emerald-600' : 'text-red-500'}>Push KV{health.bindings.push_subs_kv ? '✓' : '✗'}</span>
        <span className={health.bindings.pit_present ? 'text-emerald-600' : 'text-red-500'}>PIT{health.bindings.pit_present ? '✓' : '✗'}</span>
      </div>
      {health.config_drift.length > 0 ? (
        <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          config drift: {health.config_drift.join(' · ')}
        </div>
      ) : null}
    </div>
  );
}
